import { ORDER_STATUSES, type OrderStatus } from '@blackarrow/types';

import { isoDate } from '@/lib/date-presets';

import { DASHBOARD_TILES, type DashboardStage } from './dashboard-stages';

/**
 * The ops orders table's filter state, which lives entirely in the URL.
 *
 * Not a refinement: PLAN.md §11 names the empty topbar search slot as blocked
 * on exactly this, because a global search has to be able to say what it
 * filtered to and cannot if the filters are component state
 * (docs/ADMIN-PLAN.md slice 3). It also makes a filtered view something an
 * operator can paste into a message, which is how "which twenty parcels" gets
 * answered without a screenshot.
 *
 * The cursor is deliberately absent. A shared link should mean a filter, not a
 * position in a chain that has since moved.
 */
export interface OrdersSearch {
    status?: OrderStatus[];
    merchant?: number;
    rider?: number;
    city?: number;
    payment?: 'cod' | 'prepaid';
    /** Bare `YYYY-MM-DD`. The server widens each to a whole Karachi day. */
    from?: string;
    to?: string;
    q?: string;
    open?: boolean;
    /** Refusals on the shelf (`only`), or the shelf without them (`exclude`). */
    declined_return?: 'only' | 'exclude' | 'waiting' | 'assigned';
    /**
     * The dashboard tile this list is the answer to: parcels that had this
     * happen on those days (ADR 0044), not parcels that are in that status now.
     */
    happened?: DashboardStage;
    /** Bare `YYYY-MM-DD`, Karachi. Always set together with `happened`. */
    happened_from?: string;
    happened_to?: string;
    /**
     * Delivered on these days by `delivered_at` — the orders behind the
     * dashboard's money tiles. Bare `YYYY-MM-DD`, set together.
     */
    delivered_from?: string;
    delivered_to?: string;
}

/**
 * The same shape, but every key may be explicitly undefined.
 *
 * This is what a caller builds while it is still deciding — "no merchant
 * filter" is naturally written as `merchant: undefined`, which
 * `exactOptionalPropertyTypes` rightly refuses to call the same thing as an
 * absent key.
 */
export type Draft<T> = { [K in keyof T]?: T[K] | undefined };

/**
 * Drops the keys whose value is undefined, turning a draft into search params.
 *
 * An absent filter has to be genuinely absent: `?merchant=undefined` in the
 * URL is not a filter, it is a bug on display.
 */
export function prune<T extends object>(value: Draft<T>): T {
    return Object.fromEntries(
        Object.entries(value).filter(([, item]) => item !== undefined),
    ) as T;
}

function positiveInt(value: unknown): number | undefined {
    const parsed = Number(value);

    return Number.isInteger(parsed) && parsed > 0 ? parsed : undefined;
}

/**
 * `status` accepts a bare string as well as an array, because the dashboard's
 * stage cards have always linked in with one and those links are in people's
 * history and bookmarks.
 */
function statuses(value: unknown): OrderStatus[] | undefined {
    const raw = Array.isArray(value) ? value : [value];

    const valid = raw.filter(
        (item): item is OrderStatus =>
            typeof item === 'string' && ORDER_STATUSES.includes(item as OrderStatus),
    );

    return valid.length > 0 ? valid : undefined;
}

/**
 * Anything malformed is dropped rather than passed to the API — a hand-edited
 * URL should narrow the table or do nothing, never 422 it.
 */
export function validateOrdersSearch(search: Record<string, unknown>): OrdersSearch {
    const { status, happened, merchant, rider, city, payment, from, to, q, open, declined_return: split } =
        search;
    const tile = DASHBOARD_TILES.find((stage) => stage === happened);
    const happenedFrom = isoDate(search.happened_from);
    const happenedTo = isoDate(search.happened_to);
    const deliveredFrom = isoDate(search.delivered_from);
    const deliveredTo = isoDate(search.delivered_to);

    return prune<OrdersSearch>({
        status: statuses(status),
        merchant: positiveInt(merchant),
        rider: positiveInt(rider),
        city: positiveInt(city),
        payment: payment === 'cod' || payment === 'prepaid' ? payment : undefined,
        from: isoDate(from),
        to: isoDate(to),
        q: typeof q === 'string' && q !== '' ? q : undefined,
        open: open === true ? true : undefined,
        declined_return:
            split === 'only' || split === 'exclude' || split === 'waiting' || split === 'assigned'
                ? split
                : undefined,
        // A tile without its days means nothing, and the API would 422 it.
        ...(tile !== undefined && happenedFrom !== undefined && happenedTo !== undefined
            ? { happened: tile, happened_from: happenedFrom, happened_to: happenedTo }
            : {}),
        // Likewise: one end of the window alone would 422.
        ...(deliveredFrom !== undefined && deliveredTo !== undefined
            ? { delivered_from: deliveredFrom, delivered_to: deliveredTo }
            : {}),
    });
}
